// src/pages/AdminDashboard.js
import React, { useEffect, useState, useRef } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { format, subDays, startOfDay, endOfDay, differenceInHours } from 'date-fns';
// import { getAdminDebugInfo } from '../utils/adminUtils'; // Unused - removed
import { assignHostIdsToExistingHosts } from '../utils/hostAchievements';
import { migrateExistingUsersToEmailVerification } from '../utils/emailVerification';
import { initializeAdminClaims, getCurrentUserClaims } from '../utils/customClaims';
import { useAuth } from '../contexts/authContext';
import AdminSubscriptionFix from '../components/AdminSubscriptionFix';
import StripeConnectSetup from '../components/StripeConnectSetup';


const AdminDashboard = () => {
  const { user, claims, isAdmin, ready } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTeams: 0,
    totalGames: 0,
    totalRevenue: 0,
    activeUsers: 0,
    newUsersToday: 0,
    newTeamsToday: 0,
    gamesToday: 0,
    // Support-focused stats
    supportTickets: 0,
    openTickets: 0,
    urgentTickets: 0,
    avgResponseTime: 0,
    ticketsToday: 0,
    resolvedToday: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [urgentTickets, setUrgentTickets] = useState([]);
  const [recentSupportRequests, setRecentSupportRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminStatus, setAdminStatus] = useState(null);
  const [assigningHostIds, setAssigningHostIds] = useState(false);
  const [hostIdAssignmentResult, setHostIdAssignmentResult] = useState(null);
  const [migratingEmailVerification, setMigratingEmailVerification] = useState(false);
  const [emailVerificationResult, setEmailVerificationResult] = useState(null);
  const [initializingClaims, setInitializingClaims] = useState(false);
  const [claimsResult, setClaimsResult] = useState(null);
  const [currentClaims, setCurrentClaims] = useState(null);
  const fetchingRef = useRef(false);
  const lastFetchRef = useRef(0);
  const didFetch = useRef(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;                 // wait for auth to be ready
    if (!isAdmin) return;               // wait for admin (use comprehensive check)
    if (didFetch.current) return;       // fetch only once
    didFetch.current = true;
    
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        console.log("🔍 Fetching collections...");
        await fetchDashboardData(); // your existing function
        if (!alive) return;
        console.log("✅ Collections fetched successfully");
      } catch (e) {
        if (!alive) return;
        console.error("❌ Fetch collections failed:", e);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [isAdmin, ready]);

  const handleAssignHostIds = async () => {
    if (!window.confirm('⚠️ This will assign sequential Host IDs to all existing hosts who don\'t have one.\n\nThis action cannot be undone. Continue?')) {
      return;
    }

    try {
      setAssigningHostIds(true);
      setHostIdAssignmentResult(null);
      
      const assignedCount = await assignHostIdsToExistingHosts(db);
      
      setHostIdAssignmentResult({
        success: true,
        message: `✅ Successfully assigned Host IDs to ${assignedCount} existing hosts!`,
        count: assignedCount
      });
      
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (error) {
      console.error('Error assigning host IDs:', error);
      setHostIdAssignmentResult({
        success: false,
        message: `❌ Error assigning Host IDs: ${error.message}`,
        count: 0
      });
    } finally {
      setAssigningHostIds(false);
    }
  };

  const handleMigrateEmailVerification = async () => {
    if (!window.confirm('⚠️ This will migrate all existing users to have email verification status.\n\nThis assumes all existing users have verified emails and will mark them as verified.\n\nThis action cannot be undone. Continue?')) {
      return;
    }

    try {
      setMigratingEmailVerification(true);
      setEmailVerificationResult(null);
      
      const result = await migrateExistingUsersToEmailVerification();
      
      setEmailVerificationResult({
        success: result.success,
        message: result.message,
        migratedCount: result.migratedCount,
        errorCount: result.errorCount
      });
      
      // Refresh dashboard data
      await fetchDashboardData();
    } catch (error) {
      console.error('Error migrating email verification:', error);
      setEmailVerificationResult({
        success: false,
        message: `❌ Error migrating email verification: ${error.message}`,
        migratedCount: 0,
        errorCount: 0
      });
    } finally {
      setMigratingEmailVerification(false);
    }
  };

  const handleInitializeAdminClaims = async () => {
    if (!window.confirm('🔐 This will initialize custom claims for your admin account.\n\nThis action sets up the new authentication system. Continue?')) {
      return;
    }

    setInitializingClaims(true);
    setClaimsResult(null);
    
    try {
      console.log('🚀 Initializing admin claims...');
      const result = await initializeAdminClaims();
      setClaimsResult(result);
      
      // Get current claims to display
      const claims = await getCurrentUserClaims();
      setCurrentClaims(claims);
      
      console.log('✅ Admin claims initialized:', result);
    } catch (error) {
      console.error('❌ Error initializing claims:', error);
      setClaimsResult({
        success: false,
        error: error.message
      });
    } finally {
      setInitializingClaims(false);
    }
  };

  const handleGetCurrentClaims = async () => {
    try {
      const claims = await getCurrentUserClaims();
      setCurrentClaims(claims);
      console.log('Current user claims:', claims);
    } catch (error) {
      console.error('Error getting claims:', error);
    }
  };



  const fetchDashboardData = async () => {
    // Prevent multiple simultaneous calls
    if (fetchingRef.current) {
      console.log('🔍 fetchDashboardData: Already fetching, skipping...');
      return;
    }
    
    // Prevent too frequent calls (minimum 5 seconds between calls)
    const now = Date.now();
    if (now - lastFetchRef.current < 5000) {
      console.log('🔍 fetchDashboardData: Too soon since last fetch, skipping...');
      return;
    }
    
    try {
      fetchingRef.current = true;
      lastFetchRef.current = now;
      setError(null);
      
      // Admin status already verified in useEffect
      console.log('🔍 AdminDashboard: Fetching dashboard data for admin user:', user?.uid);
      setAdminStatus(true);
      
      // Test single document access first
      console.log('🔍 Testing single document access...');
      try {
        const testDoc = await getDoc(doc(db, 'users', user.uid));
        console.log('✅ Single document access works:', testDoc.exists());
      } catch (error) {
        console.error('❌ Single document access failed:', error);
      }

      // Fetch all collections for stats
      console.log('🔍 Fetching collections...');
      
      // Test each collection individually to see which one fails
      try {
        console.log('🔍 Testing users collection...');
        const usersSnapshot = await getDocs(collection(db, 'users'));
        console.log('✅ Users collection fetched successfully:', usersSnapshot.size, 'documents');
      } catch (error) {
        console.error('❌ Users collection failed:', error);
      }
      
      try {
        console.log('🔍 Testing games collection...');
        const gamesSnapshot = await getDocs(collection(db, 'games'));
        console.log('✅ Games collection fetched successfully:', gamesSnapshot.size, 'documents');
      } catch (error) {
        console.error('❌ Games collection failed:', error);
      }
      
      try {
        console.log('🔍 Testing supportTickets collection...');
        const ticketsSnapshot = await getDocs(collection(db, 'supportTickets'));
        console.log('✅ SupportTickets collection fetched successfully:', ticketsSnapshot.size, 'documents');
      } catch (error) {
        console.error('❌ SupportTickets collection failed:', error);
      }
      
      // Now try all together
      console.log('🔍 Fetching all collections together...');
      const [usersSnapshot, gamesSnapshot, ticketsSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'games')),
        getDocs(collection(db, 'supportTickets'))
      ]);
      console.log('✅ All collections fetched successfully');

      // Calculate basic stats
      const totalUsers = usersSnapshot.size;
      const totalTeams = usersSnapshot.docs.filter(doc => {
        const role = doc.data().role;
        return role === 'team' || role === 'team_captain';
      }).length;
      const totalGames = gamesSnapshot.size;
      const totalHosts = usersSnapshot.docs.filter(doc => doc.data().role === 'host').length;
      
      // Calculate today's stats
      const today = new Date();
      const todayStart = startOfDay(today);
      const todayEnd = endOfDay(today);
      
      const newUsersToday = usersSnapshot.docs.filter(doc => {
        const createdAt = doc.data().createdAt?.toDate?.();
        return createdAt && createdAt >= todayStart && createdAt <= todayEnd;
      }).length;

      const newTeamsToday = usersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        const createdAt = userData.createdAt?.toDate?.();
        const role = userData.role;
        return (role === 'team' || role === 'team_captain') && createdAt && createdAt >= todayStart && createdAt <= todayEnd;
      }).length;

      const gamesToday = gamesSnapshot.docs.filter(doc => {
        const gameDate = doc.data().date;
        return gameDate === format(today, 'yyyy-MM-dd');
      }).length;

      // Calculate revenue (using current pricing: $30 regular, $20 marketplace, $10 creator own)
      const totalRevenue = totalGames * 20; // Average estimated revenue per game

      // Active users (users who have been active in last 7 days)
      const sevenDaysAgo = subDays(today, 7);
      const activeUsers = usersSnapshot.docs.filter(doc => {
        const lastActive = doc.data().lastActive?.toDate?.();
        return lastActive && lastActive >= sevenDaysAgo;
      }).length;

      // Support ticket stats
      const tickets = ticketsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const supportTickets = tickets.length;
      const openTickets = tickets.filter(t => t.status === 'open').length;
      const urgentTickets = tickets.filter(t => t.priority === 'urgent' && t.status !== 'resolved').length;
      const ticketsToday = tickets.filter(t => {
        const createdAt = t.createdAt?.toDate?.();
        return createdAt && createdAt >= todayStart && createdAt <= todayEnd;
      }).length;
      const resolvedToday = tickets.filter(t => {
        const updatedAt = t.updatedAt?.toDate?.();
        return t.status === 'resolved' && updatedAt && updatedAt >= todayStart && updatedAt <= todayEnd;
      }).length;

      // Calculate average response time
      const resolvedTickets = tickets.filter(t => t.status === 'resolved' && t.createdAt && t.updatedAt);
      const totalResponseTime = resolvedTickets.reduce((total, ticket) => {
        const created = ticket.createdAt.toDate();
        const updated = ticket.updatedAt.toDate();
        return total + differenceInHours(updated, created);
      }, 0);
      const avgResponseTime = resolvedTickets.length > 0 ? Math.round(totalResponseTime / resolvedTickets.length) : 0;
      
      setStats({
        totalUsers,
        totalTeams,
        totalHosts,
        totalGames,
        totalRevenue,
        activeUsers,
        newUsersToday,
        newTeamsToday,
        gamesToday,
        supportTickets,
        openTickets,
        urgentTickets,
        avgResponseTime,
        ticketsToday,
        resolvedToday
      });

      // Fetch urgent tickets
      const urgent = tickets
        .filter(t => t.priority === 'urgent' && t.status !== 'resolved')
        .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.())
        .slice(0, 5);
      setUrgentTickets(urgent);

      // Fetch recent support requests
      const recentSupport = tickets
        .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.())
        .slice(0, 5);
      setRecentSupportRequests(recentSupport);

      // Fetch recent activity
      const recentUsers = usersSnapshot.docs
        .sort((a, b) => b.data().createdAt?.toDate?.() - a.data().createdAt?.toDate?.())
        .slice(0, 5)
        .map(doc => ({ id: doc.id, ...doc.data(), type: 'user' }));

      const recentTeams = usersSnapshot.docs
        .filter(doc => {
          const role = doc.data().role;
          return role === 'team' || role === 'team_captain';
        })
        .sort((a, b) => b.data().createdAt?.toDate?.() - a.data().createdAt?.toDate?.())
        .slice(0, 5)
        .map(doc => ({ id: doc.id, ...doc.data(), type: 'team' }));

      const recentGames = gamesSnapshot.docs
        .sort((a, b) => b.data().createdAt?.toDate?.() - a.data().createdAt?.toDate?.())
        .slice(0, 5)
        .map(doc => ({ id: doc.id, ...doc.data(), type: 'game' }));

      const allActivity = [...recentUsers, ...recentTeams, ...recentGames]
        .sort((a, b) => b.createdAt?.toDate?.() - a.createdAt?.toDate?.())
        .slice(0, 10);

      setRecentActivity(allActivity);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(`❌ Error loading dashboard: ${error.message}`);
    } finally {
      fetchingRef.current = false;
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <div className={`bg-gray-800 rounded-lg shadow-md p-6 border-l-4 ${color} border border-gray-700`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-300">{title}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className={`text-xs mt-1 ${trend > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {trend > 0 ? '↗' : '↘'} {Math.abs(trend)}% from yesterday
            </div>
          )}
        </div>
        <div className="text-3xl">{icon}</div>
      </div>
    </div>
  );

  const ActionCard = ({ title, description, icon, color, onClick, priority = 'normal', badge }) => (
    <div 
      onClick={onClick}
      className={`bg-gray-800 rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 border-l-4 ${color} border border-gray-700 ${
        priority === 'high' ? 'ring-2 ring-yellow-500' : ''
      }`}
    >
      <div className="flex items-center space-x-4">
        <div className="text-3xl">{icon}</div>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            {priority === 'high' && (
              <span className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full">Priority</span>
            )}
            {badge && (
              <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">{badge}</span>
            )}
          </div>
          <p className="text-sm text-gray-300">{description}</p>
        </div>
      </div>
    </div>
  );

  const TicketItem = ({ ticket, isUrgent = false }) => {
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'urgent': return 'text-red-400';
        case 'high': return 'text-orange-400';
        case 'medium': return 'text-yellow-400';
        default: return 'text-green-400';
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case 'open': return 'bg-red-600';
        case 'in_progress': return 'bg-yellow-600';
        case 'resolved': return 'bg-green-600';
        default: return 'bg-gray-600';
      }
    };

    return (
      <div className={`flex items-center space-x-3 p-3 bg-gray-800 rounded-lg shadow-sm border border-gray-700 ${isUrgent ? 'ring-2 ring-red-500' : ''}`}>
        <div className="text-xl">{ticket.category === 'technical' ? '🔧' : ticket.category === 'billing' ? '💰' : '❓'}</div>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-white">{ticket.subject}</p>
            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(ticket.status)}`}>
              {ticket.status.replace('_', ' ')}
            </span>
            <span className={`text-xs ${getPriorityColor(ticket.priority)}`}>
              {ticket.priority}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            {ticket.userEmail} • {ticket.createdAt?.toDate?.() ? 
              format(ticket.createdAt.toDate(), 'MMM d, h:mm a') : 
              'Recently'
            }
          </p>
        </div>
      </div>
    );
  };

  const ActivityItem = ({ activity }) => {
    const getActivityIcon = () => {
      switch (activity.type) {
        case 'user': return '👤';
        case 'team': return '🏆';
        case 'game': return '🧠';
        default: return '📝';
      }
    };

    const getActivityText = () => {
      switch (activity.type) {
        case 'user': return `New user: ${activity.fullName || activity.email}`;
        case 'team': return `New team: ${activity.name}`;
        case 'game': return `New game: ${activity.title || 'Untitled'}`;
        default: return 'New activity';
      }
    };

    return (
      <div className="flex items-center space-x-3 p-3 bg-gray-800 rounded-lg shadow-sm border border-gray-700">
        <div className="text-xl">{getActivityIcon()}</div>
        <div className="flex-1">
          <p className="text-sm font-medium text-white">{getActivityText()}</p>
          <p className="text-xs text-gray-400">
            {activity.createdAt?.toDate?.() ? 
              format(activity.createdAt.toDate(), 'MMM d, h:mm a') : 
              'Recently'
            }
          </p>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading support dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="p-8">
          <div className="bg-red-900 border border-red-700 rounded-lg p-6 text-center">
            <h2 className="text-xl font-semibold text-red-300 mb-4">❌ Admin Access Error</h2>
            <p className="text-red-200 mb-4">{error}</p>
            <div className="space-x-4">
              <button
                onClick={() => navigate('/admin/setup')}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                🔧 Admin Setup
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!adminStatus) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-gray-300">You don't have admin privileges to access this dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        {/* General Admin Dashboard Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">⚙️ Admin Dashboard</h1>
              <p className="text-gray-300">Manage your TriviaPlug platform and users</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => navigate('/admin/support-tickets')}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                🎫 Support Tickets
              </button>
              <button
                onClick={() => navigate('/admin/analytics')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                📊 Analytics
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                🔄 Refresh Dashboard
              </button>
              <button
                onClick={() => navigate('/admin/setup')}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                ⚙️ Setup & Debug
              </button>
            </div>
          </div>
        </div>

        {/* TriviaPlug Stripe Connect Setup */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">💰 Platform Payment Account</h2>
          <p className="text-gray-300 mb-4">
            Set up TriviaPlug's payment account to receive platform revenue from marketplace sales.
          </p>
          <StripeConnectSetup />
        </div>

        {/* Quick Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">📊 Analytics</h3>
                <p className="text-gray-300 text-sm mb-4">View platform analytics and metrics</p>
                <button
                  onClick={() => navigate('/admin/analytics')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  View Analytics
                </button>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">🛒 Marketplace</h3>
                <p className="text-gray-300 text-sm mb-4">Manage marketplace games and listings</p>
                <button
                  onClick={() => navigate('/admin/marketplace')}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  Manage Marketplace
                </button>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">🔒 Security</h3>
                <p className="text-gray-300 text-sm mb-4">Monitor security and user management</p>
                <button
                  onClick={() => navigate('/admin/security')}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                >
                  Security Center
                </button>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">🎫 Support</h3>
                <p className="text-gray-300 text-sm mb-4">Handle customer support tickets</p>
                <button
                  onClick={() => navigate('/admin/support-tickets')}
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm"
                >
                  Support Tickets
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fix Tool */}
        <div className="mb-8">
          <AdminSubscriptionFix />
        </div>

        {/* Support Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Open Tickets"
            value={stats.openTickets}
            icon="🎫"
            color="border-red-400"
            subtitle={`${stats.urgentTickets} urgent`}
          />
          <StatCard
            title="Avg Response Time"
            value={`${stats.avgResponseTime}h`}
            icon="⏱️"
            color="border-blue-400"
            subtitle="Hours to resolve"
          />
          <StatCard
            title="Tickets Today"
            value={stats.ticketsToday}
            icon="📅"
            color="border-green-400"
            subtitle={`${stats.resolvedToday} resolved`}
          />
          <StatCard
            title="Total Users"
            value={stats.totalUsers.toLocaleString()}
            icon="👥"
            color="border-purple-400"
            subtitle={`${stats.newUsersToday} new today`}
          />
        </div>

        {/* Urgent Tickets Alert */}
        {urgentTickets.length > 0 && (
          <div className="mb-8">
            <div className="bg-red-900 border border-red-700 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-red-300">🚨 Urgent Tickets Requiring Attention</h2>
                <button
                  onClick={() => navigate('/admin/support-tickets')}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {urgentTickets.map((ticket) => (
                  <TicketItem key={ticket.id} ticket={ticket} isUrgent={true} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Support Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">⚡ Quick Support Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ActionCard
              title="Support Tickets"
              description="Handle customer issues and support requests"
              icon="🎫"
              color="border-red-500"
              onClick={() => navigate('/admin/support-tickets')}
              priority="high"
              badge={stats.openTickets > 0 ? `${stats.openTickets} open` : null}
            />
            <ActionCard
              title="User Management"
              description="View and manage user accounts, roles, and permissions"
              icon="👥"
              color="border-blue-500"
              onClick={() => navigate('/admin/users')}
              priority="high"
            />
            <ActionCard
              title="AI Chat Support"
              description="Monitor and manage AI chat interactions"
              icon="🤖"
              color="border-purple-500"
              onClick={() => navigate('/admin/ai-chat')}
            />
            <ActionCard
              title="Security Dashboard"
              description="Monitor security events and threats"
              icon="🔒"
              color="border-red-500"
              onClick={() => navigate('/admin/security')}
              priority="high"
            />
            <ActionCard
              title="Analytics"
              description="View comprehensive platform metrics"
              icon="📊"
              color="border-purple-500"
              onClick={() => navigate('/admin/analytics')}
            />
          </div>
        </div>

        {/* Recent Support Requests */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">📋 Recent Support Requests</h2>
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-300">Latest customer support tickets</p>
              <button
                onClick={() => navigate('/admin/support-tickets')}
                className="text-blue-400 hover:text-blue-300 text-sm"
              >
                View All →
              </button>
            </div>
            <div className="space-y-3">
              {recentSupportRequests.length > 0 ? (
                recentSupportRequests.map((ticket) => (
                  <TicketItem key={ticket.id} ticket={ticket} />
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No recent support requests</p>
              )}
            </div>
          </div>
        </div>

        {/* Content Management */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">📚 Content Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ActionCard
              title="Add Game"
              description="Create new trivia game content"
              icon="➕"
              color="border-green-500"
              onClick={() => navigate('/admin/add-game')}
            />
            <ActionCard
              title="Game Marketplace"
              description="Browse and manage game content"
              icon="🛒"
              color="border-yellow-500"
              onClick={() => navigate('/admin/marketplace')}
            />
            <ActionCard
              title="Public Games"
              description="Manage games shown on Player Hub"
              icon="🎯"
              color="border-purple-500"
              onClick={() => navigate('/admin/public-games')}
            />
            <ActionCard
              title="Content Moderation"
              description="Review flagged content and manage approvals"
              icon="🛡️"
              color="border-red-500"
              onClick={() => navigate('/admin/moderation')}
            />
            <ActionCard
              title="Ads & Slides"
              description="Manage advertising content and promotions"
              icon="📢"
              color="border-orange-500"
              onClick={() => navigate('/admin/ads')}
            />
            <ActionCard
              title="Badge Management"
              description="Award and remove badges for hosts, teams, and venues"
              icon="🏆"
              color="border-yellow-500"
              onClick={() => navigate('/admin/badges')}
            />
          </div>
        </div>

        {/* Admin Utilities */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">🛠️ Admin Utilities</h2>
          <div className="bg-gray-800 rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-white">Host ID Assignment</h3>
                <p className="text-gray-300 text-sm">
                  Assign sequential Host IDs to existing hosts who don't have one yet
                </p>
              </div>
              <button
                onClick={handleAssignHostIds}
                disabled={assigningHostIds}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {assigningHostIds ? '🔄 Assigning...' : '🎤 Assign Host IDs'}
              </button>
            </div>
            
            {hostIdAssignmentResult && (
              <div className={`p-3 rounded text-sm ${
                hostIdAssignmentResult.success 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}>
                {hostIdAssignmentResult.message}
                {hostIdAssignmentResult.success && hostIdAssignmentResult.count > 0 && (
                  <div className="mt-2 text-xs">
                    Host IDs assigned: {hostIdAssignmentResult.count}
                  </div>
                )}
              </div>
            )}

            <hr className="my-6 border-gray-600" />

            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-white">Email Verification Migration</h3>
                <p className="text-gray-300 text-sm">
                  Migrate existing users to have email verification status (for new email verification system)
                </p>
              </div>
              <button
                onClick={handleMigrateEmailVerification}
                disabled={migratingEmailVerification}
                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {migratingEmailVerification ? '🔄 Migrating...' : '📧 Migrate Email Verification'}
              </button>
            </div>
            
            {emailVerificationResult && (
              <div className={`p-3 rounded text-sm ${
                emailVerificationResult.success 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}>
                {emailVerificationResult.message}
                {emailVerificationResult.success && emailVerificationResult.migratedCount > 0 && (
                  <div className="mt-2 text-xs">
                    Users migrated: {emailVerificationResult.migratedCount}
                    {emailVerificationResult.errorCount > 0 && (
                      <span className="ml-2 text-red-600">
                        Errors: {emailVerificationResult.errorCount}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}


          </div>

          {/* Custom Claims Testing */}
          <div className="mt-6 p-4 bg-gray-700 rounded-lg">
            <h3 className="text-lg font-medium text-white mb-2">🔐 Custom Claims System (New Auth)</h3>
            <p className="text-gray-300 text-sm mb-4">
              Test and initialize the new custom claims authentication system
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <button
                onClick={handleInitializeAdminClaims}
                disabled={initializingClaims}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {initializingClaims ? 'Initializing...' : '🚀 Initialize Admin Claims'}
              </button>
              
              <button
                onClick={handleGetCurrentClaims}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                📋 Get Current Claims
              </button>
            </div>

            {claimsResult && (
              <div className={`p-3 rounded text-sm mb-4 ${
                claimsResult.success 
                  ? 'bg-green-100 text-green-800 border border-green-300' 
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}>
                {claimsResult.success ? claimsResult.message : claimsResult.error}
              </div>
            )}

            {currentClaims && (
              <div className="p-3 bg-gray-600 rounded text-sm">
                <h4 className="text-white font-medium mb-2">Current Custom Claims:</h4>
                <pre className="text-gray-300 text-xs overflow-x-auto">
                  {JSON.stringify(currentClaims, null, 2)}
                </pre>
              </div>
            )}
          </div>

        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <ActivityItem key={`${activity.type}-${activity.id}-${index}`} activity={activity} />
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
